/**
 * Checks if 2 aspectratios match. Equal criteria as used in the backend (diff < 0.001)
 *
 * @param ratioOne aspectRatio, e.g. 1.69999
 * @param ratioTwo aspectRatio, e.g. 1
 * @returns boolean
 */
export function sameAspectRatio(ratioOne?: number, ratioTwo?: number): boolean {
	if (!ratioOne || !ratioTwo) {
		return false;
	}

	return Math.abs(ratioOne - ratioTwo) < 0.001;
}

export interface AspectRatioFit {
	heightRatio: number;
	widthRatio: number;
	leftRatio: number;
	topRatio: number;
}

/**
 * Compares the aspect ratio of the child (e.g. 9/16) and fits this in the parent aspect ratio (e.g. 16/9)
 * Will try to use as much area as possible and center horizontally or vertically
 *
 * @param childAspectRatio aspect ratio of the child, e.g. 0.625 (9/16)
 * @param parentAspectratio aspect ratio of the child, e.g. 1.7777 (16/9)
 */
export function fitAspectRatioInAspectRatio(childAspectRatio: number | null, parentAspectratio: number): AspectRatioFit {
	// if cropset is default (null), fill parent
	if (!childAspectRatio) {
		return { heightRatio: 1, widthRatio: 1, leftRatio: 0, topRatio: 0 };
	}

	// Dummy parent height/width for easier calc ratios
	const BASE = 100;
	const parentHeight = BASE;
	const parentWidth = BASE * parentAspectratio;

	let height = 0;
	let width = 0;
	let left = 0;
	let top = 0;

	// initial box, must fit in the parent and centered
	if (childAspectRatio < parentAspectratio) {
		// full height, limit width and h-center
		height = parentHeight;
		width = height * childAspectRatio;
		left = (parentWidth - width) / 2;
	} else {
		// full width, limit height and v-center (or full height if video and crop aspect ratio match)
		width = parentWidth;
		height = width * (1 / childAspectRatio);
		top = (parentHeight - height) / 2;
	}

	return {
		heightRatio: height / parentHeight,
		widthRatio: width / parentWidth,
		leftRatio: left / parentWidth,
		topRatio: top / parentHeight
	};
};
